import { useState, useEffect } from 'react'
import { Settings, ArrowRight, Check, Loader2 } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import toast from 'react-hot-toast'

const FLUXOS = [
  {
    value: 'nenhum',
    titulo: 'Não Controlar',
    descricao: 'O sistema não faz controle de pagamentos entre comprador e vendedor. Apenas comissões e despesas.',
    diagrama: [],
    cor: 'gray',
  },
  {
    value: 'direto',
    titulo: 'Pagamento Direto',
    descricao: 'Comprador paga diretamente ao vendedor. Uma única baixa por parcela.',
    diagrama: ['Comprador', 'Vendedor'],
    cor: 'blue',
  },
  {
    value: 'via_leiloeira',
    titulo: 'Via Leiloeira',
    descricao: 'Comprador paga à leiloeira, que repassa ao vendedor. Duas baixas por parcela (recebimento + repasse).',
    diagrama: ['Comprador', 'Leiloeira', 'Vendedor'],
    cor: 'amber',
  },
]

export default function Configuracoes() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [config, setConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selecionado, setSelecionado] = useState('direto')

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    gestaoService.obterConfiguracoes(orgId)
      .then(r => {
        const data = r.data || r
        setConfig(data)
        setSelecionado(data.fluxo_pagamento || 'direto')
      })
      .catch(() => {
        // Se não existe ainda, usa default
        setSelecionado('nenhum')
      })
      .finally(() => setLoading(false))
  }, [orgId])

  const handleSalvar = async () => {
    if (!orgId) return
    setSaving(true)
    try {
      const res = await gestaoService.atualizarConfiguracoes(orgId, {
        fluxo_pagamento: selecionado,
      })
      setConfig(res.data || res)
      toast.success('Configuração salva com sucesso')
    } catch {
      toast.error('Erro ao salvar configuração')
    } finally {
      setSaving(false)
    }
  }

  const alterou = config && selecionado !== config.fluxo_pagamento

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando configurações...
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-500" />
          Configurações da Gestão Financeira
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Defina como funciona o fluxo de pagamento entre compradores e vendedores.
        </p>
      </div>

      {/* Fluxo de Pagamento */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-1">Fluxo de Pagamento</h2>
        <p className="text-xs text-gray-500 mb-5">
          Como funciona o pagamento entre comprador e vendedor no seu leilão?
        </p>

        <div className="space-y-3">
          {FLUXOS.map(fluxo => {
            const ativo = selecionado === fluxo.value
            return (
              <button
                key={fluxo.value}
                onClick={() => setSelecionado(fluxo.value)}
                className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                  ativo
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Radio */}
                  <div className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    ativo ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                  }`}>
                    {ativo && <Check className="w-3 h-3 text-white" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${ativo ? 'text-blue-900' : 'text-gray-800'}`}>
                      {fluxo.titulo}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{fluxo.descricao}</p>

                    {/* Diagrama visual */}
                    <div className="flex items-center gap-2 mt-3">
                      {fluxo.diagrama.map((etapa, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${
                            ativo ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {etapa}
                          </span>
                          {i < fluxo.diagrama.length - 1 && (
                            <ArrowRight className={`w-3.5 h-3.5 ${ativo ? 'text-blue-400' : 'text-gray-300'}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Botão salvar */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            {alterou
              ? 'Você alterou a configuração. Clique em salvar para aplicar.'
              : 'Configuração atual aplicada.'}
          </p>
          <button
            onClick={handleSalvar}
            disabled={!alterou || saving}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Configuração'
            )}
          </button>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-amber-800 mb-1">Como funciona cada fluxo?</p>
        <ul className="text-xs text-amber-700 space-y-1.5 ml-3 list-disc">
          <li>
            <strong>Não Controlar:</strong> O módulo de Comprador x Vendedor fica desativado.
            Apenas comissões a receber e despesas do leilão são gerenciados.
          </li>
          <li>
            <strong>Direto:</strong> Ao dar baixa na parcela, o pagamento é considerado concluído.
            O comprador pagou e o vendedor recebeu em uma única operação.
          </li>
          <li>
            <strong>Via Leiloeira:</strong> A tela terá duas páginas separadas —
            <em> Comprador (Pagto)</em> e <em>Vendedor (Repasses)</em>.
            Cada parcela precisa de duas baixas.
          </li>
        </ul>
      </div>
    </div>
  )
}
